import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, CircularProgress, Grid2 } from "@mui/material";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useReferenceData } from "../../../hooks";
import { useErrorHandler } from "../../../hooks/Error/useErrorHandler";
import { useFormStyles } from "../../../hooks/useFormStyles";
import { GodAviForm as GodAviFormType, getGodAviFormSchema } from "../../../types/forms/capture";
import { VulnerabilitySelect } from "../VulnerabilitySelect";

interface GodAviFormProps {
  godAviInputs?: GodAviFormType | null;
  canEdit: boolean;
  onSubmit: (values: GodAviFormType) => Promise<void>;
}

export default function GodAviForm({
  onSubmit,
  canEdit,
  godAviInputs,
}: Readonly<GodAviFormProps>) {
  const { t } = useTranslation();
  const handleError = useErrorHandler();
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const formStyles = useFormStyles(false);

  const validationSchema = getGodAviFormSchema(t);

  const methods = useForm<GodAviFormType>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      godGId: godAviInputs?.godGId ?? undefined,
      godOId: godAviInputs?.godOId ?? undefined,
      godDId: godAviInputs?.godDId ?? undefined,
    },
  });

  useEffect(() => {
    if (godAviInputs) {
      methods.reset(godAviInputs);
    }
  }, [godAviInputs, methods]);

  const { control } = methods;

  const { data: godG = [] } = useReferenceData("god", "god_g");
  const { data: godO = [] } = useReferenceData("god", "god_o");
  const { data: godD = [] } = useReferenceData("god", "god_d");

  const handleFormSubmit = async (values: GodAviFormType) => {
    try {
      setIsSaving(true);
      await onSubmit(values);
    } catch (error) {
      handleError(error);
    } finally {
      setIsSaving(false);
    } 
  };

  const buttonLabel = godAviInputs
    ? t("generic.recalculate")
    : t("generic.calculate");

  return (
    <FormProvider {...methods}>
      <Box component="form" onSubmit={methods.handleSubmit(handleFormSubmit)}>
        <Grid2 container spacing={2}>
          <Grid2 size={4} sx={{mb: 2}}>
            <VulnerabilitySelect
              name="godGId" 
              label={`${t("calculations.vulnerability.god-avi.god-g")} *`}
              options={godG}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={4}>
            <VulnerabilitySelect 
              name="godOId" 
              label={`${t("calculations.vulnerability.god-avi.god-o")} *`}
              options={godO}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          <Grid2 size={4}>
            <VulnerabilitySelect 
              name="godDId" 
              label={`${t("calculations.vulnerability.god-avi.god-d")} *`}
              options={godD}
              control={control}
              formStyles={formStyles}
              scoreLabel={t("calculations.vulnerability.drastic-eppna.score")}
              canEdit={canEdit}
            />
          </Grid2>
          

          {/* Action Bar */}
          {canEdit && (
            <Grid2 size={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: -11 }}>
                <Button type="submit" variant="contained" sx={{ minWidth: 140, p: 1, mt: 10 }}>
                  {isSaving ? <CircularProgress size={24} /> : buttonLabel}
                </Button>
              </Box>
            </Grid2>
          )}
        </Grid2>
      </Box>
    </FormProvider>
  );
}